/**
 * Recalculation of accommodation rooms already in the offer after the global
 * markup / weekend surcharge changes (CPO decision, variant C).
 *
 * - New items keep the net base price of each night (`profitroomPrice*`) and the
 *   calculated price (`suggestedPrice`). `unitPrice !== suggestedPrice` means a
 *   manual price, which always wins: the room is skipped.
 * - Old items (no `suggestedPrice`) are explained with the current availability:
 *   the item is recalculated only when its price equals the price calculated from
 *   the current rate with the previous markup. Otherwise it is skipped, because
 *   a price that cannot be explained must not change.
 * - A room (full-stay master + children, or a single-day item) is decided as a
 *   whole: one skipped night skips the entire room.
 */

import { BatchAvailabilityResponse } from "../../../types";
import {
  MarkupSettings,
  calculateNightPriceWithMarkup,
  toFiniteNumber,
} from "../../../utils/markupCalculations";

export type OccupancyType = "pairs" | "singles_shared" | "singles_alone";

/** Net base prices of one night by occupancy. */
export interface NightBasePrices {
  single?: number;
  double?: number;
  triple?: number;
}

export interface MarkupRecalculationSummary {
  /** Rooms whose prices were recalculated. */
  recalculated: number;
  /** Rooms skipped because a night has a manually entered price. */
  skippedManual: number;
  /** Rooms skipped because their price no longer matches the current rates. */
  skippedRatesChanged: number;
}

export interface PlanItemPriceUpdate {
  dayId: string;
  itemId: string;
  changes: {
    unitPrice: number;
    suggestedPrice: number;
    profitroomPriceSingle?: number;
    profitroomPriceDouble?: number;
    profitroomPriceTriple?: number;
  };
}

export interface MarkupRecalculationPlan {
  updates: PlanItemPriceUpdate[];
  summary: MarkupRecalculationSummary;
}

/** Price difference treated as equal (decimal columns hold 2 places). */
const PRICE_EPSILON = 0.005;

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== "";

const optionalNumber = (value: unknown): number | undefined =>
  hasValue(value) && Number.isFinite(Number(value)) ? Number(value) : undefined;

/**
 * Net base price for the occupancy, same rules as the room form
 * (pairs: double, singles_alone: single, singles_shared: by persons per room).
 * Returns null when the needed rate is missing.
 */
export function selectBaseNetPrice(
  prices: NightBasePrices | null | undefined,
  occupancyType: OccupancyType | string | undefined,
  personsCount: number
): number | null {
  if (!prices) return null;
  let base: number | undefined;
  switch (occupancyType) {
    case "pairs":
      base = prices.double;
      break;
    case "singles_shared":
      if (personsCount >= 3 && hasValue(prices.triple)) base = prices.triple;
      else if (personsCount >= 2 && hasValue(prices.double)) base = prices.double;
      else base = prices.single;
      break;
    case "singles_alone":
      base = prices.single;
      break;
    default:
      base = undefined;
  }
  const value = optionalNumber(base);
  return value !== undefined && value > 0 ? value : null;
}

/** Net base prices of one availability pricing entry ({ single: { net } } ...). */
export function toNightBasePrices(
  pricing:
    | {
        single?: { net: number };
        double?: { net: number };
        triple?: { net: number };
      }
    | null
    | undefined
): NightBasePrices | null {
  if (!pricing) return null;
  const prices: NightBasePrices = {
    single: optionalNumber(pricing.single?.net),
    double: optionalNumber(pricing.double?.net),
    triple: optionalNumber(pricing.triple?.net),
  };
  return prices.single === undefined &&
    prices.double === undefined &&
    prices.triple === undefined
    ? null
    : prices;
}

const calendarDate = (date: string): string => (date || "").split("T")[0];

/** Current net rates of a room category on one offer day. */
function currentBasePrices(
  availability: BatchAvailabilityResponse | null | undefined,
  categoryOptionId: string,
  day: { id: string; date: string }
): NightBasePrices | null {
  if (!availability) return null;
  const date = calendarDate(day.date);

  // Same source as the room form: minAvailability[category].pricing[date]
  const minPricing = availability.minAvailability?.[categoryOptionId]?.pricing;
  const fromMin = toNightBasePrices(
    minPricing?.[day.date] ?? minPricing?.[date]
  );
  if (fromMin) return fromMin;

  const availabilityDay = availability.days?.find(
    (d) => d.dayId === day.id || calendarDate(d.date) === date
  );
  const room = availabilityDay?.rooms?.find(
    (r) => r.categoryOptionId === categoryOptionId
  );
  return toNightBasePrices(room?.pricing);
}

function savedBasePrices(item: any): NightBasePrices | null {
  const prices: NightBasePrices = {
    single: optionalNumber(item.profitroomPriceSingle),
    double: optionalNumber(item.profitroomPriceDouble),
    triple: optionalNumber(item.profitroomPriceTriple),
  };
  return prices.single === undefined &&
    prices.double === undefined &&
    prices.triple === undefined
    ? null
    : prices;
}

type NightDecision =
  | { kind: "manual" }
  | { kind: "unexplained" }
  | { kind: "recalculate"; update: PlanItemPriceUpdate };

function decideNight(
  item: any,
  day: { id: string; date: string },
  availability: BatchAvailabilityResponse | null | undefined,
  oldMarkup: MarkupSettings | null,
  newMarkup: MarkupSettings | null
): NightDecision {
  const unitPrice = toFiniteNumber(item.unitPrice);
  const categoryOptionId = item.categoryOption?.id;
  const personsCount =
    toFiniteNumber(item.personsCountPerRoom) ||
    toFiniteNumber(item.categoryOption?.personsPerRoom) ||
    1;
  const savedBase = savedBasePrices(item);
  const suggested = optionalNumber(item.suggestedPrice);

  if (suggested !== undefined) {
    if (Math.abs(unitPrice - suggested) > PRICE_EPSILON) {
      return { kind: "manual" };
    }
    const base = selectBaseNetPrice(savedBase, item.occupancyType, personsCount);
    if (base !== null) {
      const price = calculateNightPriceWithMarkup(base, day.date, newMarkup);
      return {
        kind: "recalculate",
        update: {
          dayId: day.id,
          itemId: item.id,
          changes: { unitPrice: price, suggestedPrice: price },
        },
      };
    }
    // Calculated price without a saved base: explain it like an old item
  }

  const currentRates = currentBasePrices(availability, categoryOptionId, day);
  const base = selectBaseNetPrice(currentRates, item.occupancyType, personsCount);
  if (base === null) return { kind: "unexplained" };

  const previousPrice = calculateNightPriceWithMarkup(base, day.date, oldMarkup);
  if (Math.abs(unitPrice - previousPrice) > PRICE_EPSILON) {
    return { kind: "unexplained" };
  }

  const price = calculateNightPriceWithMarkup(base, day.date, newMarkup);
  return {
    kind: "recalculate",
    update: {
      dayId: day.id,
      itemId: item.id,
      changes: {
        unitPrice: price,
        suggestedPrice: price,
        // Save the rates that explained the price, so the item counts as new from now on
        ...(currentRates?.single !== undefined && {
          profitroomPriceSingle: currentRates.single,
        }),
        ...(currentRates?.double !== undefined && {
          profitroomPriceDouble: currentRates.double,
        }),
        ...(currentRates?.triple !== undefined && {
          profitroomPriceTriple: currentRates.triple,
        }),
      },
    },
  };
}

/**
 * Plans the price updates of accommodation rooms already in the offer when the
 * markup changes from `oldMarkup` to `newMarkup`. Pure: no API calls.
 *
 * @param offerDays offer days with `planItems`; the last day is the check-out day
 */
export function planMarkupRecalculation(
  offerDays: Array<{ id: string; date: string; planItems?: any[] }>,
  availability: BatchAvailabilityResponse | null | undefined,
  oldMarkup: MarkupSettings | null,
  newMarkup: MarkupSettings | null
): MarkupRecalculationPlan {
  const nights = offerDays.slice(0, -1);
  const rooms = new Map<string, NightDecision[]>();

  nights.forEach((day) => {
    (day.planItems ?? [])
      .filter((item) => item?.categoryOption?.optionType === "accommodation")
      .forEach((item) => {
        const roomId = item.parentId ?? item.parent?.id ?? item.id;
        const decisions = rooms.get(roomId) ?? [];
        decisions.push(decideNight(item, day, availability, oldMarkup, newMarkup));
        rooms.set(roomId, decisions);
      });
  });

  const summary: MarkupRecalculationSummary = {
    recalculated: 0,
    skippedManual: 0,
    skippedRatesChanged: 0,
  };
  const updates: PlanItemPriceUpdate[] = [];

  rooms.forEach((decisions) => {
    if (decisions.some((d) => d.kind === "manual")) {
      summary.skippedManual += 1;
    } else if (decisions.some((d) => d.kind === "unexplained")) {
      summary.skippedRatesChanged += 1;
    } else {
      summary.recalculated += 1;
      decisions.forEach((d) => {
        if (d.kind === "recalculate") updates.push(d.update);
      });
    }
  });

  return { updates, summary };
}

/**
 * Polish message shown after the markup window is approved, or null when the
 * offer has no rooms.
 */
export function formatRecalculationMessage(
  summary: MarkupRecalculationSummary
): string | null {
  const skipped = summary.skippedManual + summary.skippedRatesChanged;
  if (summary.recalculated === 0 && skipped === 0) return null;

  const parts = [`Przeliczono pokoje: ${summary.recalculated}.`];
  if (skipped > 0) {
    const reasons: string[] = [];
    if (summary.skippedManual > 0) {
      reasons.push(`ręczna cena: ${summary.skippedManual}`);
    }
    if (summary.skippedRatesChanged > 0) {
      reasons.push(
        `cena niezgodna z bieżącymi stawkami hotelu: ${summary.skippedRatesChanged}`
      );
    }
    parts.push(
      `Pominięto: ${skipped} (${reasons.join(", ")}). ` +
        "Pominięty pokój możesz usunąć i dodać ponownie, żeby policzyć go z nowym narzutem."
    );
  }
  return parts.join(" ");
}
